import React, { useEffect, useRef, useState } from "react";
import { FiEdit2, FiMessageCircle, FiPlus, FiTrash2 } from "react-icons/fi";
import { HiXMark } from "react-icons/hi2";
import Button from "components/ui/Button";
import { useDeletePilotChatMutation, useGetPilotChatsQuery, useRenamePilotChatMutation } from "apis/pilot";
import PilotLoader from "./PilotLoader";
import { blackButton, grayButton } from "./buttonLooks";

// The admin's own chats with Pilot, newest first, drawn as the agent app's past-conversations list.

const formatChatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const differenceMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (differenceMinutes < 1) return "now";
  if (differenceMinutes < 60) return `${differenceMinutes}m`;
  const differenceHours = Math.floor(differenceMinutes / 60);
  if (differenceHours < 24) return `${differenceHours}h`;
  const differenceDays = Math.floor(differenceHours / 24);
  if (differenceDays < 7) return `${differenceDays}d`;
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

const IconCircle = ({ active, children }) => (
  <div
    className={`mr-3 flex h-9 w-9 flex-none items-center justify-center rounded-full ${
      active ? "bg-black text-white" : "bg-lightgray text-darkgray"
    }`}
  >
    {children}
  </div>
);

const rowButton =
  "ml-1 flex h-7 w-7 flex-none items-center justify-center rounded-full text-mediumgray hover:bg-lightgray hover:text-darkergray";

// The agent app's Modal, as its delete dialog draws it: the page behind held still while it is open, closed only by its
// own buttons.
const AgentModal = ({ title, onClose, children }) => {
  useEffect(() => {
    const scrollPosition = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollPosition);
    };
  }, []);

  return (
    <div
      className="custom-modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-2"
      style={{ zIndex: 10010 }}
    >
      <div
        className="relative flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-surface text-darkergray shadow-[0_24px_80px_-16px_rgba(0,0,0,0.18)]"
        style={{ maxHeight: "min(90dvh, 90vh)" }}
      >
        <div className="relative flex-shrink-0 rounded-t-2xl bg-surface px-6 pb-2 pt-5">
          <h4 className="mt-0 w-full text-center">{title}</h4>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-darkergray transition-colors hover:bg-lightgray"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>
        <div className="mx-auto min-h-0 w-full max-w-[1280px] flex-1 overflow-y-auto px-6 pb-4">{children}</div>
      </div>
    </div>
  );
};

// Closed as Delete is tapped, as the agent's is; the chat goes once the server has deleted it.
const DeleteChatModal = ({ chat, onClose, onDeleted }) => {
  const remove = useDeletePilotChatMutation();
  if (!chat) return null;
  return (
    <AgentModal title="Delete conversation" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-darkgray">This will permanently delete this conversation.</p>
        <div className="flex gap-3">
          <Button className={`${grayButton} flex-1`} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className={`${blackButton} flex-1`}
            onClick={() => {
              onClose();
              remove.mutate(chat._id, { onSuccess: () => onDeleted(chat._id) });
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </AgentModal>
  );
};

const ChatRow = ({ chat, isOpen, onOpen, onDelete }) => {
  const rename = useRenamePilotChatMutation();
  const [draft, setDraft] = useState(null);
  const discardRef = useRef(false);

  // Enter and Escape both leave through blur, so a title is saved once at most.
  const saveTitle = () => {
    const title = draft?.trim();
    setDraft(null);
    if (discardRef.current) discardRef.current = false;
    else if (title && title !== chat.title) rename.mutate({ chatId: chat._id, title });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => draft === null && onOpen(chat._id)}
      onKeyDown={(event) => {
        if (draft === null && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onOpen(chat._id);
        }
      }}
      className={`group relative flex cursor-pointer items-center border-b border-lightgray px-4 py-3 transition-colors ${
        isOpen ? "bg-surfaceSoft" : "hover:bg-surfaceSoft"
      }`}
    >
      <IconCircle active={isOpen}>
        <FiMessageCircle size={16} />
      </IconCircle>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        {draft !== null ? (
          <input
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={saveTitle}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Escape") discardRef.current = true;
              if (event.key === "Enter" || event.key === "Escape") event.currentTarget.blur();
            }}
            className="form-input min-w-0 flex-1 border border-mediumgray text-sm"
            aria-label="Chat title"
          />
        ) : (
          <p className={`min-w-0 flex-1 truncate text-sm text-darkergray ${isOpen ? "font-bold" : "font-semibold"}`}>
            {chat.title}
          </p>
        )}
        {!!chat.updatedAt && <span className="flex-none text-xs text-darkgray">{formatChatTime(chat.updatedAt)}</span>}
      </div>

      {isOpen && <span className="ml-2.5 h-1.5 w-1.5 flex-none rounded-full bg-black" />}

      {/* Over the row's end while it is pointed at or focused, so hidden they take none of the title's width. */}
      {draft === null && (
        <div className="absolute inset-y-0 right-0 hidden items-center bg-surfaceSoft pl-1 pr-4 group-focus-within:flex group-hover:flex">
          <button
            type="button"
            aria-label={`Rename ${chat.title}`}
            onClick={(event) => {
              event.stopPropagation();
              setDraft(chat.title);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            className={rowButton}
          >
            <FiEdit2 size={14} />
          </button>
          <button
            type="button"
            aria-label={`Delete ${chat.title}`}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(chat);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            className={rowButton}
          >
            <FiTrash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// A column beside the chat, or with `asPanel` the agent app's history panel drawn in the chat's place.
export default function PilotChatList({ openChatId, onOpen, onNewChat, onDeleted, asPanel = false }) {
  const { data: chats, isPending } = useGetPilotChatsQuery();
  const [deleting, setDeleting] = useState(null);
  // A new chat is already open until its first question is sent.
  const canStartNewChat = !!openChatId;

  return (
    <div
      className={
        asPanel ? "flex min-h-0 flex-1 flex-col" : "flex w-72 flex-none flex-col border-r border-mediumgray"
      }
    >
      <DeleteChatModal chat={deleting} onClose={() => setDeleting(null)} onDeleted={onDeleted} />
      <div
        className={`min-h-0 flex-1 overflow-y-auto pb-3 ${asPanel ? "mx-auto w-full max-w-2xl" : ""}`}
      >
        {/* The agent app's outline-button row: its hover shows even while it is off, and it draws every icon in its
            text colour, so the plus is dark on the black circle. */}
        <Button
          type="button"
          onClick={onNewChat}
          disabled={!canStartNewChat}
          className="flex w-full items-center border-b border-lightgray bg-surface px-4 py-3.5 text-left text-[12px] text-textPrimary transition-colors hover:bg-surfaceSoft disabled:cursor-not-allowed disabled:opacity-55"
        >
          <IconCircle active>
            <FiPlus size={17} className="text-textPrimary" />
          </IconCircle>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-darkergray">New chat</span>
            <span className="block truncate text-xs text-darkgray">
              {canStartNewChat ? "Start a fresh Pilot conversation" : "Send a message first, then start a fresh chat"}
            </span>
          </span>
        </Button>

        {isPending && (
          <div className="flex justify-center py-10">
            <PilotLoader />
          </div>
        )}

        {!isPending && chats.length === 0 && (
          <div className="flex flex-col items-center px-5 py-11 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-lightgray">
              <FiMessageCircle size={20} className="text-darkgray" />
            </div>
            <p className="text-sm font-semibold text-darkergray">No conversations yet</p>
            <p className="mt-1.5 text-xs text-darkgray">Your Pilot chats will appear here.</p>
          </div>
        )}

        {chats.map((chat) => (
          <ChatRow key={chat._id} chat={chat} isOpen={chat._id === openChatId} onOpen={onOpen} onDelete={setDeleting} />
        ))}
      </div>
    </div>
  );
}
